import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Upload, FileSpreadsheet, ArrowLeft, ArrowRight, AlertCircle, AlertTriangle,
  CheckCircle2, XCircle, Trash2, Download, ChevronRight, ShieldCheck,
  RefreshCw, Library, Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth, isAdmin } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DELETE_REASON_CHOICES } from "@/components/DeleteRecordButton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { BulkAudioIntake } from "@/components/BulkAudioIntake";

// NEO Bulk Audio Intake — lightweight mode switch shown on /intake. The
// Bulk Metadata Intake flow below is unchanged; audio mode renders the
// separate BulkAudioIntake component.
function IntakeModeSwitch({ mode, setMode }) {
  return (
    <div className="flex gap-2" data-testid="intake-mode-switch" role="tablist" aria-label="Intake mode">
      <Button
        variant={mode === "metadata" ? "default" : "outline"}
        size="sm"
        style={mode === "metadata" ? { background: "hsl(347 72% 22%)" } : undefined}
        onClick={() => setMode("metadata")}
        data-testid="intake-mode-metadata"
        role="tab"
        aria-selected={mode === "metadata"}
      >
        Bulk Metadata Intake
      </Button>
      <Button
        variant={mode === "audio" ? "default" : "outline"}
        size="sm"
        style={mode === "audio" ? { background: "hsl(347 72% 22%)" } : undefined}
        onClick={() => setMode("audio")}
        data-testid="intake-mode-audio"
        role="tab"
        aria-selected={mode === "audio"}
      >
        Bulk Audio Intake
      </Button>
    </div>
  );
}

// Phase-1 workflow steps.
const STEP_SELECT     = "select";
const STEP_UPLOAD     = "upload";
const STEP_MAP        = "map";
const STEP_REVIEW     = "review";

function rowStatusBadge(status) {
  const map = {
    "Ready for Review":    { bg: "hsl(140 38% 90%)", fg: "hsl(140 50% 22%)" },
    "Needs Attention":     { bg: "hsl(40 70% 92%)",  fg: "hsl(38 70% 28%)" },
    "Duplicate Warning":   { bg: "hsl(20 70% 90%)",  fg: "hsl(20 70% 30%)" },
    "Rejected":            { bg: "hsl(0 60% 92%)",   fg: "hsl(0 60% 30%)" },
    "Imported":            { bg: "hsl(347 50% 90%)", fg: "hsl(347 70% 22%)" },
  };
  const s = map[status] || map["Needs Attention"];
  return (
    <Badge
      variant="outline"
      style={{ background: s.bg, color: s.fg, borderColor: s.fg + "33" }}
      className="text-[10px] uppercase tracking-wide font-medium whitespace-nowrap"
    >
      {status}
    </Badge>
  );
}

function batchStatusBadge(status) {
  const map = {
    "Staged":     { bg: "hsl(225 14% 90%)", fg: "hsl(225 14% 22%)" },
    "Completed":  { bg: "hsl(140 38% 90%)", fg: "hsl(140 50% 22%)" },
    "Discarded":  { bg: "hsl(0 60% 92%)",   fg: "hsl(0 60% 30%)" },
  };
  const s = map[status] || map["Staged"];
  return (
    <Badge variant="outline"
           style={{ background: s.bg, color: s.fg, borderColor: s.fg + "33" }}
           className="text-[10px] uppercase tracking-wide">
      {status}
    </Badge>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function BulkIntake() {
  const { user } = useAuth();
  const canManage = isAdmin(user);

  // NEO Bulk Audio Intake — "metadata" (existing flow, unchanged) | "audio".
  // Deep-linkable: /intake?mode=audio preselects the audio intake, and
  // ?dest=demo|catalog preselects the audio destination mode.
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "audio" ? "audio" : "metadata");
  useEffect(() => {
    setMode(searchParams.get("mode") === "audio" ? "audio" : "metadata");
  }, [searchParams]);

  const [step, setStep] = useState(STEP_SELECT);

  // ---- Catalog Code source (live registry) ----
  const [codes, setCodes] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");

  // ---- Field metadata + parsed file ----
  const [fields, setFields] = useState([]);
  const [parsed, setParsed] = useState(null); // { headers, rows, suggested_mapping, worksheets, worksheet_name, source_kind, source_filename }
  const [parsing, setParsing] = useState(false);
  const [mapping, setMapping] = useState({}); // { field_key: source_header }

  // ---- Active batch under review ----
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [activeBatch, setActiveBatch] = useState(null);
  const [activeRows, setActiveRows] = useState([]);
  const [loadingBatch, setLoadingBatch] = useState(false);

  // ---- Recent batches list ----
  const [recentBatches, setRecentBatches] = useState([]);

  // ---- Dialog state ----
  const [editingRow, setEditingRow] = useState(null);
  const [rejectingRow, setRejectingRow] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [confirmingDup, setConfirmingDup] = useState(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardReason, setDiscardReason] = useState("");
  const [discardOther, setDiscardOther] = useState("");

  const fileInputRef = useRef(null);

  // ------------------------------ Initial load ------------------------------
  useEffect(() => {
    if (!canManage) return;
    (async () => {
      try {
        const [codesR, fieldsR, batchesR] = await Promise.all([
          api.get("/catalog-codes", { params: { active_only: true } }),
          api.get("/intake/fields"),
          api.get("/intake/batches"),
        ]);
        setCodes(Array.isArray(codesR.data) ? codesR.data : []);
        setFields(Array.isArray(fieldsR.data?.fields) ? fieldsR.data.fields : []);
        setRecentBatches(Array.isArray(batchesR.data) ? batchesR.data : []);
      } catch (e) {
        toast.error(errMsg(e, "Could not load intake setup."));
      }
    })();
  }, [canManage]);

  async function refreshRecentBatches() {
    try {
      const r = await api.get("/intake/batches");
      setRecentBatches(Array.isArray(r.data) ? r.data : []);
    } catch (_e) { /* soft-fail */ }
  }

  async function loadBatch(batchId) {
    setLoadingBatch(true);
    try {
      const r = await api.get(`/intake/batches/${batchId}`);
      setActiveBatchId(batchId);
      setActiveBatch(r.data.batch);
      setActiveRows(Array.isArray(r.data.rows) ? r.data.rows : []);
      setStep(STEP_REVIEW);
    } catch (e) {
      toast.error(errMsg(e, "Could not load batch."));
    } finally {
      setLoadingBatch(false);
    }
  }

  // ------------------------------ Upload + parse ------------------------------
  async function handleFileSelected(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!selectedCode) {
      toast.error("Select a Catalog Code first.");
      return;
    }
    const lower = f.name.toLowerCase();
    if (!(lower.endsWith(".csv") || lower.endsWith(".xlsx"))) {
      toast.error("Only .csv and .xlsx files are accepted.");
      e.target.value = "";
      return;
    }
    setParsing(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/intake/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(r.data);
      // Adopt suggested mapping as the starting point.
      setMapping(r.data.suggested_mapping || {});
      setStep(STEP_MAP);
    } catch (err) {
      toast.error(errMsg(err, "Could not parse file."));
    } finally {
      setParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function reparseWithWorksheet(wsName) {
    if (!fileInputRef.current?.files?.[0]) {
      // No raw file in memory anymore — the user must re-pick. Show prompt.
      toast.error("Please re-select the file to switch worksheet.");
      return;
    }
    const fd = new FormData();
    fd.append("file", fileInputRef.current.files[0]);
    setParsing(true);
    try {
      const r = await api.post(`/intake/parse?worksheet=${encodeURIComponent(wsName)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(r.data);
      setMapping(r.data.suggested_mapping || {});
    } catch (err) {
      toast.error(errMsg(err, "Could not parse worksheet."));
    } finally {
      setParsing(false);
    }
  }

  // ------------------------------ Stage batch ------------------------------
  async function stageBatch() {
    if (!selectedCode) { toast.error("Catalog Code is required."); return; }
    if (!parsed?.rows?.length) { toast.error("No rows to stage."); return; }
    if (!mapping.track_title) { toast.error("Map a column to Track Title before staging."); return; }
    try {
      const body = {
        catalog_code: selectedCode,
        source_filename: parsed.source_filename,
        source_kind: parsed.source_kind,
        worksheet_name: parsed.worksheet_name || "",
        column_mapping: mapping,
        rows: parsed.rows,
      };
      const r = await api.post("/intake/batches", body);
      toast.success(`Staged ${r.data.total_rows} row(s) for review.`);
      await refreshRecentBatches();
      await loadBatch(r.data.id);
      setParsed(null);
      setMapping({});
    } catch (err) {
      toast.error(errMsg(err, "Could not stage batch."));
    }
  }

  // ------------------------------ Row actions ------------------------------
  async function approveRow(row, confirmDuplicate = false) {
    try {
      await api.post(`/intake/batches/${activeBatchId}/rows/${row.id}/approve`, {
        confirm_duplicate: confirmDuplicate,
      });
      toast.success("Row approved and live track created.");
      await loadBatch(activeBatchId);
      await refreshRecentBatches();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.duplicate_of_track_ids) {
        // Re-trigger confirmation flow
        setConfirmingDup(row);
      } else if (detail?.errors) {
        toast.error("Row has blocking errors — fix them before approving.");
      } else {
        toast.error(errMsg(err, "Could not approve row."));
      }
    }
  }

  async function rejectRow(row) {
    try {
      await api.post(`/intake/batches/${activeBatchId}/rows/${row.id}/reject`, {
        reason: rejectReason,
      });
      toast.success("Row rejected.");
      setRejectingRow(null);
      setRejectReason("");
      await loadBatch(activeBatchId);
      await refreshRecentBatches();
    } catch (err) {
      toast.error(errMsg(err, "Could not reject row."));
    }
  }

  async function approveAllReady() {
    try {
      const r = await api.post(`/intake/batches/${activeBatchId}/approve-ready`);
      toast.success(`Imported ${r.data.imported_count} ready row(s).`);
      await loadBatch(activeBatchId);
      await refreshRecentBatches();
    } catch (err) {
      toast.error(errMsg(err, "Could not approve ready rows."));
    }
  }

  async function discardBatch() {
    if (!discardReason) return;
    if (discardReason === "Other" && (discardOther || "").trim().length < 3) return;
    try {
      const finalReason = discardReason === "Other" ? `Other: ${discardOther.trim()}` : discardReason;
      const qs = new URLSearchParams();
      qs.set("reason", finalReason);
      qs.set("force", "true");
      await api.delete(`/intake/batches/${activeBatchId}?${qs.toString()}`);
      toast.success("Batch discarded.");
      setDiscardOpen(false);
      setDiscardReason(""); setDiscardOther("");
      setActiveBatchId(null); setActiveBatch(null); setActiveRows([]);
      await refreshRecentBatches();
      setStep(STEP_SELECT);
    } catch (err) {
      toast.error(errMsg(err, "Could not discard batch."));
    }
  }

  async function saveRowEdit(rowId, newValues) {
    try {
      await api.put(`/intake/batches/${activeBatchId}/rows/${rowId}`, {
        mapped_values: newValues,
      });
      toast.success("Row updated.");
      setEditingRow(null);
      await loadBatch(activeBatchId);
    } catch (err) {
      toast.error(errMsg(err, "Could not save row."));
    }
  }

  // ------------------------------ Template downloads ------------------------------
  async function downloadTemplate(kind) {
    try {
      const r = await api.get(`/intake/template.${kind}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kandake_import_template.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errMsg(err, "Could not download template."));
    }
  }

  // ------------------------------ Derived ------------------------------
  const selectedCodeMeta = codes.find((c) => c.code === selectedCode);
  const headers = parsed?.headers || [];
  const previewRows = (parsed?.rows || []).slice(0, 10);
  const requiredFieldsOk = !!mapping.track_title;

  const fieldOptionsForSelect = useMemo(
    () => fields.map((f) => ({
      value: f.key,
      label: `${f.label}${f.required ? " *" : ""}`,
      required: !!f.required,
      type: f.type,
    })),
    [fields]
  );

  // ------------------------------ Render guards ------------------------------
  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="intake-readonly">
        <Alert>
          <ShieldCheck className="w-4 h-4" />
          <AlertDescription>
            Bulk Metadata Intake is restricted to Super Admin accounts.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // NEO Bulk Audio Intake — audio mode early-return. The metadata flow
  // below remains byte-for-byte the existing experience.
  if (mode === "audio") {
    return (
      <div className="space-y-6" data-testid="bulk-intake-page">
        <IntakeModeSwitch mode={mode} setMode={setMode} />
        <BulkAudioIntake codes={codes} initialDestination={searchParams.get("dest") === "demo" ? "demo" : "catalog"} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bulk-intake-page">
      <IntakeModeSwitch mode={mode} setMode={setMode} />
      {/* ------------ Header ------------ */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
            Catalog · Phase 1 Beta
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight flex items-center gap-3">
            <Inbox className="w-7 h-7" style={{ color: "hsl(347 60% 22%)" }} />
            Bulk Metadata Intake
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Upload a CSV or XLSX of track metadata, review each row, and approve into
            Kandake. <span className="font-medium text-foreground">Nothing becomes a live track
            until you explicitly approve it.</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadTemplate("xlsx")}
                  data-testid="intake-template-xlsx">
            <Download className="w-4 h-4 mr-2" />
            Template (XLSX)
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadTemplate("csv")}
                  data-testid="intake-template-csv">
            <Download className="w-4 h-4 mr-2" />
            Template (CSV)
          </Button>
        </div>
      </div>

      {/* ------------ Stepper ------------ */}
      <Stepper step={step} hasBatch={!!activeBatchId} />

      {/* ============================ STEP: SELECT + UPLOAD ============================ */}
      {(step === STEP_SELECT || step === STEP_UPLOAD) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">1. Select Catalog Code and upload spreadsheet</CardTitle>
            <CardDescription>
              Every track in the batch will be assigned the chosen Catalog Code. Only ACTIVE
              codes can be selected.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cc-pick">Catalog Code *</Label>
                <Select value={selectedCode} onValueChange={setSelectedCode}>
                  <SelectTrigger id="cc-pick" data-testid="intake-catalog-code-select">
                    <SelectValue placeholder="Select a Catalog Code…" />
                  </SelectTrigger>
                  <SelectContent>
                    {codes.length === 0 && (
                      <div className="px-3 py-2 text-[12px] text-muted-foreground">
                        No active Catalog Codes. <Link to="/catalog-codes" className="underline">Open registry</Link>.
                      </div>
                    )}
                    {codes.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        [{c.code}] {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Archived codes are hidden — they cannot accept new tracks.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Selected for this batch</Label>
                {selectedCodeMeta ? (
                  <div className="rounded-md border p-3 bg-secondary/40">
                    <div className="text-sm font-medium">[{selectedCodeMeta.code}] {selectedCodeMeta.name}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {selectedCodeMeta.catalog_class}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-md border p-3 text-[12px] text-muted-foreground">
                    No code selected.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-md border border-dashed p-6 text-center bg-secondary/30">
              <FileSpreadsheet className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <div className="text-sm font-medium">Upload a .csv or .xlsx file</div>
              <div className="text-[12px] text-muted-foreground mt-1 max-w-md mx-auto">
                Max 8 MB. The first populated row is treated as the header. For XLSX, a worksheet
                named <code className="font-mono text-[11px]">Kandake Import Queue</code> is preferred.
              </div>
              <div className="mt-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFileSelected}
                  className="hidden"
                  data-testid="intake-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!selectedCode || parsing}
                  data-testid="intake-pick-file-button"
                  style={{ background: "hsl(347 60% 22%)" }}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {parsing ? "Parsing…" : (selectedCode ? "Choose file" : "Select a Catalog Code first")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============================ STEP: MAP ============================ */}
      {step === STEP_MAP && parsed && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-lg">2. Map columns to Kandake fields</CardTitle>
              <CardDescription>
                Confirm or adjust how spreadsheet columns map to Track fields. Track Title is required.
                Unmapped columns are kept as reference metadata on each staged row.
              </CardDescription>
            </div>
            <div className="text-[12px] text-muted-foreground">
              File: <span className="font-mono">{parsed.source_filename || "(no name)"}</span>
              {" · "}
              {parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"}
              {parsed.source_kind === "xlsx" && parsed.worksheet_name &&
                <> · sheet <span className="font-mono">{parsed.worksheet_name}</span></>}
            </div>
          </CardHeader>
          <CardContent className="space-y-5">

            {parsed.source_kind === "xlsx" && parsed.worksheets?.length > 1 && (
              <div className="flex items-end gap-2">
                <div className="min-w-[260px]">
                  <Label className="text-xs">Worksheet</Label>
                  <Select value={parsed.worksheet_name} onValueChange={reparseWithWorksheet}>
                    <SelectTrigger data-testid="intake-worksheet-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {parsed.worksheets.map((w) => (
                        <SelectItem key={w} value={w}>{w}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3" data-testid="intake-mapping-grid">
              {headers.map((h) => {
                const mappedField = Object.entries(mapping).find(([_, src]) => src === h)?.[0] || "";
                return (
                  <div key={h} className="grid grid-cols-12 items-center gap-2 border rounded-md p-2">
                    <div className="col-span-5 min-w-0">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Source</div>
                      <div className="font-mono text-[13px] truncate" title={h}>{h}</div>
                    </div>
                    <ChevronRight className="col-span-1 w-4 h-4 text-muted-foreground mx-auto" />
                    <div className="col-span-6 min-w-0">
                      <Select
                        value={mappedField}
                        onValueChange={(newField) => {
                          // Remove any existing mapping to this source column or to the new field.
                          const next = { ...mapping };
                          Object.entries(next).forEach(([k, v]) => { if (v === h || k === newField) delete next[k]; });
                          if (newField && newField !== "__unmap__") {
                            next[newField] = h;
                          }
                          setMapping(next);
                        }}
                      >
                        <SelectTrigger data-testid={`intake-map-${h}`}>
                          <SelectValue placeholder="(unmapped — kept as reference)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unmap__">(unmapped — kept as reference)</SelectItem>
                          {fieldOptionsForSelect.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label} <span className="text-muted-foreground">({opt.type})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>

            {!requiredFieldsOk && (
              <Alert variant="destructive" data-testid="intake-map-required-warning">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  Map one column to <span className="font-medium">Track Title</span> before staging.
                </AlertDescription>
              </Alert>
            )}

            <div className="mt-4 space-y-2">
              <div className="text-[12px] uppercase tracking-wide text-muted-foreground">
                Preview — first {Math.min(10, previewRows.length)} row(s)
              </div>
              <div className="border rounded-md overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px]">#</TableHead>
                      {headers.map((h) => (
                        <TableHead key={h} className="min-w-[120px]">
                          <div className="font-mono text-[11px]">{h}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {(() => {
                              const f = Object.entries(mapping).find(([_, src]) => src === h)?.[0];
                              const meta = fields.find((x) => x.key === f);
                              return meta ? `→ ${meta.label}` : "→ reference";
                            })()}
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRows.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{i + 1}</TableCell>
                        {headers.map((h) => (
                          <TableCell key={h} className="text-[12px] max-w-[220px] truncate">
                            {r[h] || <span className="text-muted-foreground italic">—</span>}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => { setStep(STEP_SELECT); setParsed(null); }}
                      data-testid="intake-back-to-select">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={stageBatch}
                disabled={!requiredFieldsOk || !parsed.rows.length}
                data-testid="intake-stage-batch"
                style={{ background: "hsl(347 60% 22%)" }}
              >
                Stage {parsed.rows.length} row(s) for review
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============================ STEP: REVIEW ============================ */}
      {step === STEP_REVIEW && activeBatch && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-3">
                3. Review queue
                {batchStatusBadge(activeBatch.status)}
              </CardTitle>
              <CardDescription>
                [{activeBatch.catalog_code}] {activeBatch.catalog_name}
                {activeBatch.source_filename && <> · <span className="font-mono">{activeBatch.source_filename}</span></>}
                {activeBatch.worksheet_name && <> · sheet <span className="font-mono">{activeBatch.worksheet_name}</span></>}
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => loadBatch(activeBatchId)} data-testid="intake-refresh">
                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                Refresh
              </Button>
              {activeBatch.status === "Staged" && activeBatch.ready_count > 0 && (
                <Button onClick={approveAllReady} data-testid="intake-approve-ready-button"
                        style={{ background: "hsl(140 50% 30%)" }}>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approve {activeBatch.ready_count} Ready row(s)
                </Button>
              )}
              {activeBatch.status === "Staged" && (
                <Button variant="outline" onClick={() => setDiscardOpen(true)} data-testid="intake-discard-batch"
                        className="text-destructive hover:text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Discard batch
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <BatchStats batch={activeBatch} />

            <div className="border rounded-md overflow-auto">
              <Table data-testid="intake-review-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[55px]">Row</TableHead>
                    <TableHead className="w-[100px]">Catalog Code</TableHead>
                    <TableHead>Track Title</TableHead>
                    <TableHead className="w-[160px]">Artist</TableHead>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead>Flags</TableHead>
                    <TableHead className="w-[220px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingBatch && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell>
                    </TableRow>
                  )}
                  {!loadingBatch && activeRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground" data-testid="intake-rows-empty">
                        This batch has no remaining staged rows.
                      </TableCell>
                    </TableRow>
                  )}
                  {!loadingBatch && activeRows.map((row) => (
                    <RowItem
                      key={row.id}
                      row={row}
                      catalogCode={activeBatch.catalog_code}
                      batchStatus={activeBatch.status}
                      onApprove={() => approveRow(row, false)}
                      onConfirmDup={() => setConfirmingDup(row)}
                      onReject={() => { setRejectingRow(row); setRejectReason(""); }}
                      onEdit={() => setEditingRow(row)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>

            <Alert>
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription className="text-[12px]">
                Imported rows become live tracks under their Catalog Code at the Incomplete clearance
                stage and follow the existing readiness/compliance workflow. They are NOT auto-published to
                Ready Tracks or any public catalogue.
              </AlertDescription>
            </Alert>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => { setStep(STEP_SELECT); setActiveBatchId(null); setActiveBatch(null); setActiveRows([]); }}
                      data-testid="intake-leave-review">
                <ArrowLeft className="w-4 h-4 mr-2" /> Done — back to upload
              </Button>
              <Button asChild variant="outline" data-testid="intake-view-tracks">
                <Link to={`/tracks`}>
                  <Library className="w-4 h-4 mr-2" />
                  View Catalog / Tracks
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============================ Recent batches ============================ */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent intake batches</CardTitle>
          <CardDescription>Staged, completed, and discarded batches across all operators.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="intake-recent-batches">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">Catalog</TableHead>
                <TableHead>File</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[90px] text-right">Rows</TableHead>
                <TableHead className="w-[90px] text-right">Imported</TableHead>
                <TableHead className="w-[160px]">Created</TableHead>
                <TableHead className="w-[110px] text-right">Open</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentBatches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                    No intake batches yet.
                  </TableCell>
                </TableRow>
              )}
              {recentBatches.map((b) => (
                <TableRow key={b.id} data-testid={`intake-batch-row-${b.id}`}>
                  <TableCell className="font-mono text-xs">{b.catalog_code}</TableCell>
                  <TableCell className="text-[12px]">
                    <div className="truncate max-w-[260px]" title={b.source_filename}>{b.source_filename || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{b.source_kind?.toUpperCase()}{b.worksheet_name ? ` · ${b.worksheet_name}` : ""}</div>
                  </TableCell>
                  <TableCell>{batchStatusBadge(b.status)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{b.total_rows}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{b.imported_count}</TableCell>
                  <TableCell className="text-[11px]">
                    <div>{fmtDate(b.created_at)}</div>
                    <div className="text-muted-foreground truncate max-w-[160px]" title={b.created_by_email}>{b.created_by_name || b.created_by_email}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => loadBatch(b.id)} data-testid={`intake-batch-open-${b.id}`}>
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ---- Dialogs ---- */}
      <EditRowDialog
        row={editingRow}
        fields={fields}
        onClose={() => setEditingRow(null)}
        onSave={(vals) => saveRowEdit(editingRow.id, vals)}
      />

      <AlertDialog open={!!rejectingRow} onOpenChange={(o) => { if (!o) setRejectingRow(null); }}>
        <AlertDialogContent data-testid="intake-reject-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reject row {rejectingRow?.source_row_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              Rejected rows will not create a live track. Provide a brief internal reason for the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Reason (optional)</Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="e.g. missing rights documents; client withdrew track"
              data-testid="intake-reject-reason"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="intake-reject-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => rejectRow(rejectingRow)}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="intake-reject-confirm"
            >
              Reject row
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmingDup} onOpenChange={(o) => { if (!o) setConfirmingDup(null); }}>
        <AlertDialogContent data-testid="intake-dup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm duplicate import?</AlertDialogTitle>
            <AlertDialogDescription>
              This row was flagged as a likely duplicate of {confirmingDup?.duplicate_of_track_ids?.length || 0}{" "}
              existing track(s) under <span className="font-mono">{activeBatch?.catalog_code}</span>.
              Approving will create a new live track regardless. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="intake-dup-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const r = confirmingDup;
                setConfirmingDup(null);
                approveRow(r, true);
              }}
              data-testid="intake-dup-confirm"
            >
              Yes, approve anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={discardOpen} onOpenChange={(o) => { setDiscardOpen(o); if (!o) { setDiscardReason(""); setDiscardOther(""); } }}>
        <AlertDialogContent data-testid="intake-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this batch?</AlertDialogTitle>
            <AlertDialogDescription>
              All staged rows that have not yet been imported will be removed. Already-imported tracks
              remain live. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Reason (required · captured in audit)
              </Label>
              <Select value={discardReason} onValueChange={setDiscardReason}>
                <SelectTrigger className="mt-1 text-[12.5px]" data-testid="intake-discard-reason-trigger">
                  <SelectValue placeholder="Select a reason…" />
                </SelectTrigger>
                <SelectContent>
                  {DELETE_REASON_CHOICES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {discardReason === "Other" && (
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Explanation (required when reason is Other)
                </Label>
                <Textarea
                  value={discardOther}
                  onChange={(e) => setDiscardOther(e.target.value)}
                  rows={2}
                  className="mt-1 text-[12.5px]"
                  data-testid="intake-discard-reason-other"
                />
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={discardBatch}
              disabled={!discardReason || (discardReason === "Other" && (discardOther || "").trim().length < 3)}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="intake-discard-confirm"
            >
              Discard batch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// --------------------------- Sub-components ---------------------------

function Stepper({ step, hasBatch }) {
  const items = [
    { id: STEP_SELECT, label: "Catalog & Upload" },
    { id: STEP_MAP, label: "Column Mapping" },
    { id: STEP_REVIEW, label: "Review & Approve" },
  ];
  const currentIdx = items.findIndex((i) => i.id === step);
  return (
    <ol className="flex flex-wrap items-center gap-2" data-testid="intake-stepper">
      {items.map((it, i) => {
        const done = i < currentIdx || (hasBatch && it.id === STEP_REVIEW && step === STEP_REVIEW);
        const active = it.id === step;
        return (
          <li key={it.id} className="flex items-center">
            <div
              className={[
                "px-3 py-1.5 rounded-full text-[12px] uppercase tracking-wide border",
                active ? "bg-foreground text-background border-foreground" :
                done ? "bg-secondary text-foreground border-foreground/20" :
                       "bg-transparent text-muted-foreground border-muted-foreground/30",
              ].join(" ")}
              data-testid={`intake-step-${it.id}`}
            >
              {i + 1}. {it.label}
            </div>
            {i < items.length - 1 && <ChevronRight className="w-4 h-4 mx-1 text-muted-foreground" />}
          </li>
        );
      })}
    </ol>
  );
}

function BatchStats({ batch }) {
  const tiles = [
    { label: "Total", value: batch.total_rows },
    { label: "Ready", value: batch.ready_count, color: "hsl(140 50% 30%)" },
    { label: "Needs Attention", value: batch.needs_attention_count, color: "hsl(38 70% 40%)" },
    { label: "Duplicates", value: batch.duplicate_warning_count, color: "hsl(20 70% 40%)" },
    { label: "Rejected", value: batch.rejected_count, color: "hsl(0 60% 40%)" },
    { label: "Imported", value: batch.imported_count, color: "hsl(347 60% 30%)" },
  ];
  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="intake-batch-stats">
      {tiles.map((t) => (
        <div key={t.label} className="border rounded-md p-2.5">
          <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{t.label}</div>
          <div className="font-display text-xl" style={{ color: t.color }}>{t.value}</div>
        </div>
      ))}
    </div>
  );
}

function RowItem({ row, catalogCode, batchStatus, onApprove, onConfirmDup, onReject, onEdit }) {
  const mv = row.mapped_values || {};
  const canEdit = row.status !== "Imported" && batchStatus !== "Discarded";
  const canApprove = ["Ready for Review", "Duplicate Warning", "Needs Attention"].includes(row.status)
                    && row.errors?.length === 0 && batchStatus !== "Discarded";
  const canReject = row.status !== "Imported" && row.status !== "Rejected" && batchStatus !== "Discarded";
  return (
    <TableRow data-testid={`intake-row-${row.id}`}>
      <TableCell className="font-mono text-xs">{row.source_row_number}</TableCell>
      <TableCell className="font-mono text-xs">{catalogCode}</TableCell>
      <TableCell>
        <div className="font-medium" data-testid={`intake-row-${row.id}-title`}>{mv.track_title || <span className="italic text-destructive">(missing)</span>}</div>
        {row.imported_track_id && (
          <div className="text-[10px] text-muted-foreground mt-0.5">
            → <span className="font-mono">{row.imported_track_id.slice(0, 8)}…</span>
          </div>
        )}
      </TableCell>
      <TableCell className="text-[12px]">{mv.artist || "—"}</TableCell>
      <TableCell>{rowStatusBadge(row.status)}</TableCell>
      <TableCell>
        {row.errors?.length > 0 && (
          <div className="text-[11px] text-destructive mb-1 flex items-start gap-1" data-testid={`intake-row-${row.id}-errors`}>
            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
            <div>{row.errors.join(" · ")}</div>
          </div>
        )}
        {row.warnings?.length > 0 && (
          <div className="text-[11px] text-amber-700 flex items-start gap-1" data-testid={`intake-row-${row.id}-warnings`}>
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <div>{row.warnings.join(" · ")}</div>
          </div>
        )}
        {row.rejection_reason && (
          <div className="text-[11px] text-muted-foreground italic">Reason: {row.rejection_reason}</div>
        )}
        {!row.errors?.length && !row.warnings?.length && !row.rejection_reason && (
          <span className="text-[11px] text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="inline-flex items-center gap-1 flex-wrap justify-end">
          {canEdit && (
            <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`intake-row-edit-${row.id}`}>
              Edit
            </Button>
          )}
          {canApprove && row.status === "Duplicate Warning" && (
            <Button size="sm" variant="outline" onClick={onConfirmDup}
                    data-testid={`intake-row-approve-${row.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              Approve
            </Button>
          )}
          {canApprove && row.status !== "Duplicate Warning" && (
            <Button size="sm" variant="outline" onClick={onApprove}
                    data-testid={`intake-row-approve-${row.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              Approve
            </Button>
          )}
          {canReject && (
            <Button size="sm" variant="ghost" onClick={onReject} className="text-destructive hover:text-destructive"
                    data-testid={`intake-row-reject-${row.id}`}>
              <XCircle className="w-3.5 h-3.5 mr-1" />
              Reject
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function EditRowDialog({ row, fields, onClose, onSave }) {
  const [values, setValues] = useState({});

  useEffect(() => {
    if (row) {
      const seed = {};
      fields.forEach((f) => {
        const v = row.mapped_values?.[f.key];
        if (Array.isArray(v)) seed[f.key] = v.join("; ");
        else if (v === null || v === undefined) seed[f.key] = "";
        else seed[f.key] = String(v);
      });
      setValues(seed);
    }
  }, [row, fields]);

  if (!row) return null;

  function setField(k, v) { setValues((s) => ({ ...s, [k]: v })); }

  function buildPayload() {
    const out = {};
    fields.forEach((f) => {
      const raw = values[f.key] ?? "";
      if (f.type === "list") {
        out[f.key] = raw.split(/\s*[;|]\s*/).filter(Boolean);
      } else {
        out[f.key] = raw;
      }
    });
    return out;
  }

  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="intake-edit-row-dialog">
        <DialogHeader>
          <DialogTitle>Edit row {row.source_row_number}</DialogTitle>
          <DialogDescription>
            Adjust mapped values for this staged row. Changes are validated when saved.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label htmlFor={`edit-${f.key}`} className="text-[12px]">
                {f.label}{f.required && <span className="text-destructive ml-0.5">*</span>}
              </Label>
              <Input
                id={`edit-${f.key}`}
                value={values[f.key] || ""}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.type === "list" ? "Use ; to separate" : ""}
                data-testid={`intake-edit-${f.key}`}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="intake-edit-cancel">Cancel</Button>
          <Button onClick={() => onSave(buildPayload())} data-testid="intake-edit-save"
                  style={{ background: "hsl(347 60% 22%)" }}>
            Save row
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
